import * as THREE from 'three';
import {
    ComputeSnapDistancesInput,
    ApplyBackToBackPositioningInput,
    ComputeDistancePosInput,
    ApplyNonBackToBackPositioningInput,
    SnapDir,
    SnapDirValue,
} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {CabinetMesh} from 'components/customer/RoomPlanner/types.d';
import {calculateToeKickScale} from 'components/customer/RoomPlanner/hardware/useAdjustableLegs';
import {DEFAULT_SNAPPING_DISTANCE} from 'components/customer/RoomPlanner/helpers/snapping/constants';

export const applyNonBackToBackPositioning = (
    input: ApplyNonBackToBackPositioningInput
) => {
    const {
        proposedPos,
        cabinetGroup,
        otherProduct,
        cabinetRotations,
        currentSnappingDir,
        isFillerProduct,
        shouldSnapToRightReturnProduct,
        shouldSnapParallelReturnSides,
        isBulkheadSnapping,
        isFloorProductSnapping,
        isFloorProduct,
        isPanelProduct,
        isPanelProductOther,
        exteriorThickness,
        otherProductExtThickness,
        carcaseThickness,
        snappedProduct,
        otherCabinetDepth,
        otherCabinetWidth,
        kickerFrameEdge,
        otherProductKickerFrameEdge,
        hasOverlappingKickerFrame,
        latestWidth,
        latestDepth,
        latestHeight,
        isFacingRightAndSnapToLShaped,
        isFacingLeftAndSnapToRightReturn,
        isKickLength,
        selectedCabinetDirection,
        otherCabinetDirection,
    } = input;
    let {allowDirections, snapped} = input;

    const selectedCabinetDepth =
        Number(cabinetGroup.userData.depth) || latestDepth;
    const selectedCabinetWidth =
        Number(cabinetGroup.userData.width) || latestWidth;

    if (cabinetRotations.selected.isNormal) {
        if (
            !isFillerProduct &&
            !shouldSnapToRightReturnProduct &&
            (currentSnappingDir === SnapDir.Right ||
                currentSnappingDir === SnapDir.Left)
        ) {
            proposedPos.z =
                proposedPos.z - (otherCabinetDepth - selectedCabinetDepth);
            if (isBulkheadSnapping) {
                const values = (cabinetGroup as CabinetMesh).userData.values;
                let offset = 0;
                if (values.cabinet_edge_W1) {
                    offset = exteriorThickness * 0.2;
                }
                proposedPos.y = proposedPos.y + latestHeight + 0.3;
                proposedPos.x = otherProduct.position.x + offset;
                proposedPos.z -= exteriorThickness * 0.2;
            }
            if (
                isFloorProductSnapping &&
                !otherProduct.userData.isFloorProduct
            ) {
                // Placeholders have no widthOffset — treat missing as 0
                proposedPos.x =
                    proposedPos.x +
                    (Number(cabinetGroup.userData.widthOffset) || 0);
            }
            if (
                isFloorProductSnapping &&
                otherProduct.userData.isFloorProduct
            ) {
                if (
                    kickerFrameEdge?.left &&
                    currentSnappingDir === SnapDir.Right
                ) {
                    proposedPos.x -=
                        (otherProductExtThickness - exteriorThickness) * 0.2;
                }
                if (
                    otherProductKickerFrameEdge?.right &&
                    currentSnappingDir === SnapDir.Left
                ) {
                    proposedPos.x +=
                        (exteriorThickness - otherProductExtThickness) * 0.2;
                }
                if (
                    otherProductKickerFrameEdge?.left === false &&
                    kickerFrameEdge?.left &&
                    currentSnappingDir === SnapDir.Right
                ) {
                    proposedPos.x += otherProductExtThickness * 0.2;
                }
            }
            if (
                !isFloorProductSnapping &&
                otherProduct.userData.isFloorProduct
            ) {
                proposedPos.x = proposedPos.x - exteriorThickness * 0.2;
            }
            if (isKickLength) {
                proposedPos.z = otherProduct.position.z;
            }
        }
        if (currentSnappingDir === SnapDir.Left) {
            proposedPos.x = proposedPos.x - latestWidth + snappedProduct.width;
        }
        if (
            shouldSnapToRightReturnProduct &&
            currentSnappingDir === SnapDir.Back
        ) {
            if (otherProduct.userData.hasRightReturnRightHangDoor) {
                proposedPos.z =
                    proposedPos.z +
                    latestDepth -
                    otherProductExtThickness * 0.2;
                proposedPos.x = proposedPos.x + otherProductExtThickness * 0.2;
            } else {
                proposedPos.z =
                    proposedPos.z -
                    snappedProduct.depth +
                    (latestDepth - snappedProduct.depth) -
                    otherProductExtThickness * 0.2;
                proposedPos.x =
                    proposedPos.x +
                    latestWidth +
                    otherProductExtThickness * 0.2;
            }
            allowDirections = [SnapDir.Back];
        }
        // When the selected product is filler product
        if (
            isFillerProduct &&
            (currentSnappingDir === SnapDir.Right ||
                currentSnappingDir === SnapDir.Left)
        ) {
            proposedPos.x += latestWidth;
        }
        // When the selected product is panel product and the other product is not panel product
        if (
            (currentSnappingDir === SnapDir.Right ||
                currentSnappingDir === SnapDir.Left) &&
            isPanelProduct &&
            !isPanelProductOther
        ) {
            proposedPos.x = proposedPos.x + latestWidth;
        }
        // When the other product is panel product and the selected product is not panel product
        if (
            (currentSnappingDir === SnapDir.Right ||
                currentSnappingDir === SnapDir.Left) &&
            !isPanelProduct &&
            isPanelProductOther
        ) {
            proposedPos.x = proposedPos.x - otherProductExtThickness * 0.2;
        }
        if (
            isFloorProductSnapping &&
            currentSnappingDir === SnapDir.Front &&
            !otherProduct.userData.noBottom &&
            !hasOverlappingKickerFrame &&
            !otherProduct.userData.isFloorProduct
        ) {
            allowDirections = [SnapDir.Front];
        }
    } else if (cabinetRotations.selected.isRight) {
        if (isBulkheadSnapping) {
            const values = (cabinetGroup as CabinetMesh).userData.values;
            let offset = 0;
            if (values.cabinet_edge_W1) {
                offset = exteriorThickness * 0.2;
            }
            if (currentSnappingDir === SnapDir.Left) {
                allowDirections = [SnapDir.Left];
            }
            if (isKickLength) {
                proposedPos.z = otherProduct.position.z;
            }
            if (currentSnappingDir === SnapDir.Front) {
                proposedPos.z =
                    proposedPos.z - (snappedProduct.depth * 2 - latestDepth);
                allowDirections = [SnapDir.Front];
            }
            proposedPos.y = proposedPos.y + latestHeight + 0.3;
            proposedPos.x = otherProduct.position.x - offset;
        }
        // For manual panels rotated RIGHT, mirror the LEFT rotation logic
        else if (isKickLength) {
            if (currentSnappingDir === SnapDir.Back) {
                // Mirror of LEFT: use positive Z offset instead of negative
                proposedPos.z =
                    proposedPos.z + (latestDepth - snappedProduct.depth);
            }
            if (
                currentSnappingDir === SnapDir.Front ||
                currentSnappingDir === SnapDir.Back
            ) {
                proposedPos.x =
                    proposedPos.x + selectedCabinetDepth - otherCabinetDepth;
            }
        } else {
            if (currentSnappingDir === SnapDir.Front) {
                proposedPos.z =
                    proposedPos.z - (snappedProduct.depth - latestDepth);
            }
            if (currentSnappingDir === SnapDir.Back) {
                proposedPos.z =
                    proposedPos.z - (latestDepth - snappedProduct.depth);
            }
            if (
                currentSnappingDir === SnapDir.Back ||
                currentSnappingDir === SnapDir.Front
            ) {
                proposedPos.x =
                    proposedPos.x - (otherCabinetDepth - selectedCabinetDepth);
            }
            // When the selected product is panel product and the other product is not panel product
            if (
                (currentSnappingDir === SnapDir.Back ||
                    currentSnappingDir === SnapDir.Front) &&
                isPanelProduct &&
                !isPanelProductOther
            ) {
                proposedPos.z = proposedPos.z - latestDepth;
            }
            // When the other product is panel product and the selected product is not panel product
            if (
                (currentSnappingDir === SnapDir.Back ||
                    currentSnappingDir === SnapDir.Front) &&
                !isPanelProduct &&
                isPanelProductOther
            ) {
                proposedPos.z = proposedPos.z + otherProductExtThickness * 0.2;
            }
            allowDirections = [SnapDir.Front, SnapDir.Back];
            if (isFloorProduct && currentSnappingDir === SnapDir.Right) {
                allowDirections.push(SnapDir.Right);
            }
            if (
                isFloorProductSnapping &&
                currentSnappingDir === SnapDir.Right
            ) {
                allowDirections.push(SnapDir.Right);
            }
        }
    } else if (
        cabinetRotations.selected.isLeft &&
        !isFacingLeftAndSnapToRightReturn
    ) {
        if (isBulkheadSnapping) {
            const values = (cabinetGroup as CabinetMesh).userData.values;
            let offset = 0;
            if (values.cabinet_edge_W1) {
                offset = exteriorThickness * 0.2;
            }
            if (
                currentSnappingDir === SnapDir.Left ||
                currentSnappingDir === SnapDir.Right
            ) {
                proposedPos.z += offset;
                allowDirections = [SnapDir.Left];
            }
            if (currentSnappingDir === SnapDir.Back) {
                proposedPos.z =
                    proposedPos.z -
                    (latestDepth - snappedProduct.depth * 2) +
                    offset;
                allowDirections = [SnapDir.Back];
            }
            proposedPos.y = proposedPos.y + latestHeight + 0.3;
            proposedPos.x = otherProduct.position.x;
        } else {
            if (currentSnappingDir === SnapDir.Front) {
                proposedPos.z =
                    proposedPos.z - (snappedProduct.depth - latestDepth);
            }
            if (currentSnappingDir === SnapDir.Back) {
                proposedPos.z =
                    proposedPos.z - (latestDepth - snappedProduct.depth);
            }
            if (
                currentSnappingDir === SnapDir.Front ||
                currentSnappingDir === SnapDir.Back
            ) {
                proposedPos.x =
                    proposedPos.x - selectedCabinetDepth + otherCabinetDepth;
            }
            allowDirections = [SnapDir.Front, SnapDir.Back];
            // When the selected product is panel product and the other product is not panel product
            if (
                (currentSnappingDir === SnapDir.Back ||
                    currentSnappingDir === SnapDir.Front) &&
                isPanelProduct &&
                !isPanelProductOther
            ) {
                proposedPos.z = proposedPos.z + latestDepth;
            }
            // When the other product is panel product and the selected product is not panel product
            if (
                (currentSnappingDir === SnapDir.Back ||
                    currentSnappingDir === SnapDir.Front) &&
                !isPanelProduct &&
                isPanelProductOther
            ) {
                proposedPos.z = proposedPos.z - otherProductExtThickness * 0.2;
            }
            if (isFloorProduct && currentSnappingDir === SnapDir.Left) {
                allowDirections.push(SnapDir.Left);
            }
            if (isFloorProductSnapping && currentSnappingDir === SnapDir.Left) {
                allowDirections.push(SnapDir.Left);
            }
        }
    } else {
        if (isBulkheadSnapping) {
            const values = (cabinetGroup as CabinetMesh).userData.values;
            let offset = 0;
            if (values.cabinet_edge_W1) {
                offset = exteriorThickness * 0.2;
            }
            if (currentSnappingDir === SnapDir.Right) {
                proposedPos.x =
                    proposedPos.x -
                    otherCabinetWidth * 2 +
                    selectedCabinetWidth -
                    offset;
                allowDirections = [SnapDir.Right];
            } else {
                proposedPos.x = proposedPos.x + otherCabinetWidth - offset;
                allowDirections = [SnapDir.Left];
            }
            proposedPos.y = proposedPos.y + latestHeight + 0.3;
            proposedPos.z += offset;
        } else {
            if (currentSnappingDir === SnapDir.Right) {
                proposedPos.x =
                    proposedPos.x + latestWidth - snappedProduct.width;
            }
            if (
                currentSnappingDir === SnapDir.Right ||
                currentSnappingDir === SnapDir.Left
            ) {
                proposedPos.z =
                    proposedPos.z + (otherCabinetDepth - selectedCabinetDepth);
            }
            // When the selected product is panel product and the other product is not panel product
            if (
                (currentSnappingDir === SnapDir.Right ||
                    currentSnappingDir === SnapDir.Left) &&
                isPanelProduct &&
                !isPanelProductOther
            ) {
                proposedPos.x = proposedPos.x - latestWidth;
            }
            // When the other product is panel product and the selected product is not panel product
            if (
                (currentSnappingDir === SnapDir.Right ||
                    currentSnappingDir === SnapDir.Left) &&
                !isPanelProduct &&
                isPanelProductOther
            ) {
                proposedPos.x = proposedPos.x + otherProductExtThickness * 0.2;
            }
            if (isFloorProductSnapping && currentSnappingDir === SnapDir.Back) {
                allowDirections.push(SnapDir.Back);
            }
        }
    }
    // Custom snapping for L-shaped cabinets
    if (isFacingRightAndSnapToLShaped && currentSnappingDir === SnapDir.Front) {
        proposedPos.x =
            proposedPos.x +
            selectedCabinetDepth +
            (otherCabinetDepth - selectedCabinetDepth);
        proposedPos.z = proposedPos.z + 4;
        if (isPanelProduct) {
            proposedPos.x = proposedPos.x + latestDepth;
        }
    }
    // Custom snapping for return cabinets
    if (
        isFacingLeftAndSnapToRightReturn &&
        currentSnappingDir === SnapDir.Front
    ) {
        proposedPos.z =
            proposedPos.z - latestDepth - (snappedProduct.depth - latestDepth);
        proposedPos.x =
            proposedPos.x -
            (latestWidth - snappedProduct.width) -
            selectedCabinetDepth +
            (selectedCabinetDepth - otherCabinetDepth) +
            carcaseThickness * 0.2;
        allowDirections = [SnapDir.Front];
    }
    // Parallel side-snap onto return/blind (both facing left or both facing right)
    if (
        shouldSnapParallelReturnSides &&
        (currentSnappingDir === SnapDir.Front ||
            currentSnappingDir === SnapDir.Back)
    ) {
        const returnSideSnapZ = getReturnSideSnapZ(
            otherProduct,
            cabinetGroup,
            currentSnappingDir
        );
        if (returnSideSnapZ !== null) {
            proposedPos.z = returnSideSnapZ;
        }
    }
    // Manual panel (kick length) snapping adjustment
    // Organized by other cabinet rotation and snap direction for easy customization
    if (isKickLength) {
        // Get the bounding box of the other cabinet
        const otherCabinetBox = new THREE.Box3().setFromObject(otherProduct);
        // Helper function to get toe kick offset
        const getToeKickOffset = () => {
            const isOtherProductTall = otherProduct.userData
                .isTallProduct as boolean;
            const baseOffset = isOtherProductTall ? 18 : 12;
            const otherProductToeKickHeight = otherProduct.userData
                .toeKickHeight as number;
            const toeKickYScale = calculateToeKickScale(
                otherProductToeKickHeight
            );
            return baseOffset * toeKickYScale;
        };
        // Determine other cabinet rotation direction
        const otherRotation = cabinetRotations.other.isRight
            ? 'right'
            : cabinetRotations.other.isLeft
              ? 'left'
              : cabinetRotations.other.isBack
                ? 'back'
                : 'normal';
        // Derive a named snap direction from SnapDir for the switch key
        const snapDirName: Record<SnapDirValue, string> = {
            [SnapDir.Right]: 'right',
            [SnapDir.Left]: 'left',
            [SnapDir.Front]: 'front',
            [SnapDir.Back]: 'back',
        };
        const snapDirection =
            snapDirName[currentSnappingDir as number as SnapDirValue];
        // Calculate toe kick offset once at the top
        const toeKickOffset = getToeKickOffset();
        // Switch based on other cabinet rotation and snap direction
        switch (`${otherRotation}_${snapDirection}`) {
            // ========== OTHER CABINET ROTATED RIGHT ==========
            case 'right_front':
                // Other cabinet rotated RIGHT, snapping to FRONT
                proposedPos.z = cabinetRotations.selected.isBack
                    ? otherCabinetBox.min.z
                    : otherCabinetBox.max.z;
                proposedPos.x =
                    otherCabinetBox.max.x -
                    otherProductExtThickness * 0.2 -
                    toeKickOffset;
                break;
            case 'right_back':
                // Other cabinet rotated RIGHT, snapping to BACK
                proposedPos.z = cabinetRotations.selected.isBack
                    ? otherCabinetBox.min.z
                    : otherCabinetBox.max.z;
                proposedPos.x =
                    otherCabinetBox.max.x -
                    otherProductExtThickness * 0.2 -
                    toeKickOffset;
                break;
            // ========== OTHER CABINET ROTATED LEFT ==========
            case 'left_front':
                // Other cabinet rotated LEFT, snapping to FRONT
                proposedPos.z = cabinetRotations.selected.isBack
                    ? otherCabinetBox.min.z
                    : otherCabinetBox.max.z;
                proposedPos.x =
                    otherCabinetBox.min.x +
                    otherProductExtThickness * 0.2 +
                    toeKickOffset;
                break;
            case 'left_back':
                // Other cabinet rotated LEFT, snapping to BACK
                proposedPos.z = cabinetRotations.selected.isBack
                    ? otherCabinetBox.min.z
                    : otherCabinetBox.max.z;
                proposedPos.x =
                    otherCabinetBox.min.x +
                    otherProductExtThickness * 0.2 +
                    toeKickOffset;
                break;
            // ========== OTHER CABINET NORMAL (facing front) ==========
            case 'normal_front':
                // Other cabinet NORMAL, snapping to FRONT
                if (cabinetRotations.selected.isRight) {
                    // Selected cabinet facing RIGHT
                    const otherCabinetRightX = otherCabinetBox.max.x;
                    proposedPos.x = otherCabinetRightX;
                    proposedPos.z =
                        otherProduct.position.z -
                        otherProductExtThickness * 0.2 -
                        toeKickOffset;
                } else {
                    // Selected cabinet NORMAL/BACK/LEFT
                    const otherCabinetFrontX = otherCabinetBox.min.x;
                    proposedPos.x = otherCabinetFrontX + 0.2;
                    proposedPos.z = otherProduct.position.z - toeKickOffset;
                }
                break;
            case 'normal_back':
                // Other cabinet NORMAL, snapping to BACK
                if (cabinetRotations.selected.isRight) {
                    // Selected cabinet facing RIGHT
                    const otherCabinetLeftX = otherCabinetBox.min.x;
                    proposedPos.x =
                        otherCabinetLeftX -
                        latestWidth / 2 -
                        otherProductExtThickness * 0.2;
                    proposedPos.z = otherProduct.position.z;
                } else {
                    // Selected cabinet NORMAL/BACK/LEFT
                    const otherCabinetFrontX = otherCabinetBox.min.x;
                    proposedPos.x = otherCabinetFrontX + 0.2;
                    const otherCabinetBackZ = otherCabinetBox.min.z;
                    proposedPos.z =
                        otherCabinetBackZ +
                        latestDepth / 2 -
                        otherProductExtThickness * 0.2;
                }
                break;
            // ========== OTHER CABINET ROTATED BACK ==========
            case 'back_front':
                // Other cabinet BACK, snapping to FRONT
                if (cabinetRotations.selected.isRight) {
                    // Selected cabinet facing RIGHT
                    const otherCabinetRightX = otherCabinetBox.max.x;
                    proposedPos.x = otherCabinetRightX;
                    proposedPos.z = otherProduct.position.z + toeKickOffset;
                } else {
                    // Selected cabinet NORMAL/BACK/LEFT
                    const otherCabinetFrontX = otherCabinetBox.min.x;
                    proposedPos.x = otherCabinetFrontX + 0.2;
                    proposedPos.z = otherProduct.position.z - toeKickOffset;
                }
                break;
            case 'back_back':
                // Other cabinet BACK, snapping to BACK
                const otherCabinetFrontX = otherCabinetBox.min.x;
                proposedPos.x = otherCabinetFrontX + 0.2;
                proposedPos.z = otherProduct.position.z + toeKickOffset;
                break;
            default:
                // Fallback for any unhandled combinations
                break;
        }
    }

    if (shouldSnapToRightReturnProduct) {
        snapped = currentSnappingDir === SnapDir.Back;
    } else if (isFacingLeftAndSnapToRightReturn) {
        snapped = currentSnappingDir === SnapDir.Front;
    } else if (isKickLength) {
        // For manual panels with perpendicular rotations, only allow front/back snapping
        const isPerpendicular =
            selectedCabinetDirection !== otherCabinetDirection;
        const isFrontOrBackSnap =
            currentSnappingDir === SnapDir.Front ||
            currentSnappingDir === SnapDir.Back;
        const isSideSnap =
            currentSnappingDir === SnapDir.Right ||
            currentSnappingDir === SnapDir.Left;
        if (isPerpendicular && isFrontOrBackSnap) {
            // Perpendicular + front/back = ALLOW
            snapped = true;
        } else if (isPerpendicular && isSideSnap) {
            // Perpendicular + side = BLOCK
            snapped = false;
        } else if (!isPerpendicular) {
            // Same direction = allow based on allowDirections or front/back
            snapped =
                allowDirections.includes(currentSnappingDir) ||
                isFrontOrBackSnap;
        }
    } else {
        snapped = snapped || allowDirections.includes(currentSnappingDir);
    }

    return {allowDirections, snapped};
};

export const applyBackToBackPositioning = (
    input: ApplyBackToBackPositioningInput
) => {
    const {
        proposedPos,
        backToBackData,
        cabinetRotations,
        currentSnappingDir,
        exteriorThickness,
        carcaseThickness,
        latestWidth,
        latestDepth,
        kickerFrameEdge,
        otherProductKickerFrameEdge,
        isFloorProductSnapping,
    } = input;
    let {allowDirections, snapped} = input;

    const offset = 7;
    const floorAdjustment = exteriorThickness * 0.2 * 2;
    const floorAdjustmentOffset =
        (exteriorThickness - 16.5) * 0.2 + (carcaseThickness - 16.5) * 0.2;
    // Configuration for each named direction's positioning rules
    const directionConfig: Partial<
        Record<
            SnapDirValue,
            {
                condition: () => boolean;
                positionAdjustment: {x: number; z: number};
                floorAdjustment: {x: number; z: number};
            }
        >
    > = {
        [SnapDir.Right]: {
            condition: () =>
                !backToBackData.isFrontAndBack &&
                cabinetRotations.selected.isRight,
            positionAdjustment: {
                x: latestWidth - offset,
                z: latestDepth,
            },
            floorAdjustment: {
                x:
                    (kickerFrameEdge?.back && otherProductKickerFrameEdge?.back
                        ? -floorAdjustment
                        : 0) - floorAdjustmentOffset,
                z:
                    kickerFrameEdge?.back && otherProductKickerFrameEdge?.back
                        ? -floorAdjustment
                        : 0,
            },
        },
        [SnapDir.Left]: {
            condition: () =>
                !backToBackData.isFrontAndBack &&
                cabinetRotations.selected.isLeft,
            positionAdjustment: {
                x: -latestWidth + offset,
                z: -latestDepth,
            },
            floorAdjustment: {
                x:
                    (kickerFrameEdge?.back && otherProductKickerFrameEdge?.back
                        ? floorAdjustment
                        : 0) + floorAdjustmentOffset,
                z:
                    kickerFrameEdge?.back && otherProductKickerFrameEdge?.back
                        ? floorAdjustment
                        : 0,
            },
        },
        [SnapDir.Front]: {
            condition: () =>
                backToBackData.isFrontAndBack &&
                cabinetRotations.selected.isNormal,
            positionAdjustment: {
                x: -latestWidth,
                z: latestDepth - offset,
            },
            floorAdjustment: {
                x: kickerFrameEdge?.back ? floorAdjustment : 0,
                z:
                    (otherProductKickerFrameEdge?.back
                        ? kickerFrameEdge?.back
                            ? -floorAdjustment
                            : -floorAdjustment / 2
                        : kickerFrameEdge?.back
                          ? -floorAdjustment / 2
                          : 0) - floorAdjustmentOffset,
            },
        },
        [SnapDir.Back]: {
            condition: () =>
                backToBackData.isFrontAndBack &&
                cabinetRotations.selected.isBack,
            positionAdjustment: {
                x: latestWidth,
                z: -latestDepth + offset,
            },
            floorAdjustment: {
                x: otherProductKickerFrameEdge?.back
                    ? kickerFrameEdge?.back
                        ? -floorAdjustment
                        : 0
                    : kickerFrameEdge?.back
                      ? -floorAdjustment
                      : 0,
                z:
                    (kickerFrameEdge?.back
                        ? otherProductKickerFrameEdge?.back
                            ? floorAdjustment
                            : floorAdjustment / 2
                        : otherProductKickerFrameEdge?.back
                          ? floorAdjustment / 2
                          : 0) + floorAdjustmentOffset,
            },
        },
    };
    const config =
        directionConfig[currentSnappingDir as number as SnapDirValue];
    if (config && config.condition()) {
        // Apply position adjustments
        proposedPos.x += config.positionAdjustment.x;
        proposedPos.z += config.positionAdjustment.z;
        snapped = true;
        // Apply floor product adjustments
        if (isFloorProductSnapping) {
            proposedPos.x += config.floorAdjustment.x;
            proposedPos.z += config.floorAdjustment.z;
        }
    }

    return {allowDirections, snapped};
};

export const computeDistancePos = (input: ComputeDistancePosInput) => {
    const {
        targetPos,
        isBackToBack,
        backToBackData,
        cabinetRotations,
        currentSnappingDir,
        snappedProduct,
        latestWidth,
        latestDepth,
        latestHeight,
        isKickLength,
        isFillerProduct,
        isFloorProductSnapping,
        isSelectedCornerAndLShapedCabinet,
        isOtherCornerAndLShapedCabinet,
        productSize,
        otherProduct,
        isBulkheadSnapping,
        isUpperSnappingToTall,
        isUpperSnappingToAnotherUpper,
        shouldSnapToRightReturnProduct,
        shouldSnapParallelReturnSides,
    } = input;

    let distancePos = targetPos.clone();
    let kickerFrontDistancePos = targetPos.clone();
    let kickerFrontRightDistancePos = targetPos.clone();

    // Modify the anchor position for snapping
    if (isBackToBack) {
        if (
            !backToBackData.isFrontAndBack &&
            cabinetRotations.selected.isRight
        ) {
            distancePos = new THREE.Vector3(
                targetPos.x - latestWidth / 2,
                targetPos.y,
                targetPos.z - latestDepth
            );
        }
        if (
            !backToBackData.isFrontAndBack &&
            cabinetRotations.selected.isLeft
        ) {
            distancePos = new THREE.Vector3(
                targetPos.x + latestWidth / 2,
                targetPos.y,
                targetPos.z + latestDepth
            );
        }
        if (
            backToBackData.isFrontAndBack &&
            cabinetRotations.selected.isNormal
        ) {
            distancePos = new THREE.Vector3(
                targetPos.x + latestWidth,
                targetPos.y,
                targetPos.z - latestDepth / 2
            );
        }
        if (backToBackData.isFrontAndBack && cabinetRotations.selected.isBack) {
            distancePos = new THREE.Vector3(
                targetPos.x - latestWidth,
                targetPos.y,
                targetPos.z + latestDepth / 2
            );
        }
    } else {
        if (cabinetRotations.selected.isNormal) {
            if (currentSnappingDir === SnapDir.Right) {
                distancePos = new THREE.Vector3(
                    targetPos.x,
                    targetPos.y,
                    targetPos.z +
                        (isFillerProduct
                            ? 0
                            : snappedProduct.depth - latestDepth)
                );
            }
            if (currentSnappingDir === SnapDir.Left) {
                distancePos = new THREE.Vector3(
                    targetPos.x - (snappedProduct.width - latestWidth),
                    targetPos.y,
                    targetPos.z +
                        (isFillerProduct
                            ? 0
                            : snappedProduct.depth - latestDepth)
                );
            }
            if (
                isFloorProductSnapping &&
                currentSnappingDir === SnapDir.Front
            ) {
                kickerFrontDistancePos = new THREE.Vector3(
                    targetPos.x - snappedProduct.width / 2,
                    targetPos.y,
                    targetPos.z
                );
                kickerFrontRightDistancePos = new THREE.Vector3(
                    targetPos.x + latestWidth / 2,
                    targetPos.y,
                    targetPos.z
                );
            }
        }
        if (cabinetRotations.selected.isRight) {
            // For manual panels rotated RIGHT, mirror the LEFT rotation logic
            if (isKickLength) {
                // Get the bounding box of the other cabinet for accurate snap detection
                const otherBox = new THREE.Box3().setFromObject(otherProduct);
                if (currentSnappingDir === SnapDir.Front) {
                    // Front snap: detect when panel is near the RIGHT side + FRONT of cabinet
                    distancePos = new THREE.Vector3(
                        otherBox.max.x + latestWidth / 2,
                        targetPos.y,
                        targetPos.z - latestDepth
                    );
                }
                if (currentSnappingDir === SnapDir.Back) {
                    // Back snap: detect when panel is near the LEFT side + FRONT of cabinet
                    distancePos = new THREE.Vector3(
                        otherBox.min.x - latestWidth / 2,
                        targetPos.y,
                        targetPos.z
                    );
                }
            } else {
                if (currentSnappingDir === SnapDir.Front) {
                    distancePos = new THREE.Vector3(
                        targetPos.x + (snappedProduct.width - latestWidth),
                        targetPos.y,
                        targetPos.z + (snappedProduct.depth - latestDepth)
                    );
                }
                if (
                    currentSnappingDir === SnapDir.Left &&
                    productSize > 200 &&
                    !isOtherCornerAndLShapedCabinet
                ) {
                    distancePos = new THREE.Vector3(
                        targetPos.x - latestWidth,
                        targetPos.y,
                        targetPos.z - latestDepth
                    );
                }
                if (
                    currentSnappingDir === SnapDir.Front &&
                    isOtherCornerAndLShapedCabinet
                ) {
                    distancePos = new THREE.Vector3(
                        targetPos.x - 50,
                        targetPos.y,
                        targetPos.z + (snappedProduct.depth - latestDepth)
                    );
                }
                // Fix L-shaped cabinet left/right face distance positioning
                if (isOtherCornerAndLShapedCabinet) {
                    if (currentSnappingDir === SnapDir.Right) {
                        // Right face of L-shaped cabinet
                        distancePos = new THREE.Vector3(
                            targetPos.x,
                            targetPos.y,
                            targetPos.z - latestDepth
                        );
                    } else if (currentSnappingDir === SnapDir.Left) {
                        // Left face of L-shaped cabinet
                        distancePos = new THREE.Vector3(
                            targetPos.x - latestWidth,
                            targetPos.y,
                            targetPos.z - latestDepth
                        );
                    }
                }
                if (currentSnappingDir === SnapDir.Back) {
                    distancePos = new THREE.Vector3(
                        targetPos.x + (snappedProduct.width - latestWidth),
                        targetPos.y,
                        targetPos.z - (snappedProduct.depth - latestDepth)
                    );
                }
            }
        }
        if (cabinetRotations.selected.isLeft) {
            if (currentSnappingDir === SnapDir.Front) {
                distancePos = new THREE.Vector3(
                    targetPos.x - (snappedProduct.width - latestWidth),
                    targetPos.y,
                    targetPos.z + (snappedProduct.depth - latestDepth)
                );
                if (isSelectedCornerAndLShapedCabinet) {
                    const distanceZ = isOtherCornerAndLShapedCabinet
                        ? targetPos.z + latestDepth + snappedProduct.depth
                        : targetPos.z + latestDepth;
                    distancePos = new THREE.Vector3(
                        targetPos.x - snappedProduct.width,
                        targetPos.y,
                        distanceZ
                    );
                }
            }
            if (currentSnappingDir === SnapDir.Back) {
                distancePos = new THREE.Vector3(
                    targetPos.x - (snappedProduct.width - latestWidth),
                    targetPos.y,
                    targetPos.z - (snappedProduct.depth - latestDepth)
                );
            }
        }
        if (cabinetRotations.selected.isBack) {
            if (currentSnappingDir === SnapDir.Left) {
                distancePos = new THREE.Vector3(
                    targetPos.x,
                    targetPos.y,
                    targetPos.z - (snappedProduct.depth - latestDepth)
                );
            }
            if (currentSnappingDir === SnapDir.Right) {
                distancePos = new THREE.Vector3(
                    targetPos.x + (snappedProduct.width - latestWidth),
                    targetPos.y,
                    targetPos.z - (snappedProduct.depth - latestDepth)
                );
            }
            if (
                currentSnappingDir === SnapDir.Back &&
                isSelectedCornerAndLShapedCabinet
            ) {
                const distanceX = isOtherCornerAndLShapedCabinet
                    ? targetPos.x - snappedProduct.width
                    : targetPos.x - snappedProduct.width * 0.5;
                distancePos = new THREE.Vector3(
                    distanceX,
                    targetPos.y,
                    targetPos.z - latestWidth - snappedProduct.depth
                );
            }
        }
    }
    if (
        (isUpperSnappingToTall || isUpperSnappingToAnotherUpper) &&
        isBulkheadSnapping &&
        !isKickLength
    ) {
        distancePos.y -= latestHeight;
    }
    if (shouldSnapToRightReturnProduct) {
        let targetPosZ = targetPos.z + snappedProduct.depth + latestDepth;
        let targetPozX = targetPos.x - latestWidth;
        if (otherProduct.userData.hasRightReturnRightHangDoor) {
            targetPosZ = targetPos.z - snappedProduct.depth;
            targetPozX = targetPos.x - (snappedProduct.width - latestWidth);
        }
        distancePos = new THREE.Vector3(targetPozX, targetPos.y, targetPosZ);
    }
    if (
        shouldSnapParallelReturnSides &&
        (currentSnappingDir === SnapDir.Front ||
            currentSnappingDir === SnapDir.Back)
    ) {
        distancePos = targetPos.clone();
    }

    return {
        distancePos,
        kickerFrontDistancePos,
        kickerFrontRightDistancePos,
    };
};

export const computeProposedPos = (
    otherProduct: THREE.Group,
    dir: THREE.Vector3,
    size: THREE.Vector3
) => {
    const axis = dir.clone().multiply(size);
    return otherProduct.position.clone().add(axis);
};

/**
 * Flush side-snap Z from real AABBs (Front/Back along world ±Z).
 * Used when either cabinet is a return/blind so pivot/AABB size diffs do not
 * leave a gap or cause overlap.
 * @param {THREE.Group} otherProduct - Cabinet being snapped to
 * @param {THREE.Group} cabinetGroup - Cabinet being dragged
 * @param {SnapDirValue} currentSnappingDir - SnapDir.Front or SnapDir.Back
 * @return {number|null} World Z, or null when direction is not Front/Back
 */
export const getReturnSideSnapZ = (
    otherProduct: THREE.Group,
    cabinetGroup: THREE.Group,
    currentSnappingDir: SnapDirValue
): number | null => {
    if (
        currentSnappingDir !== SnapDir.Front &&
        currentSnappingDir !== SnapDir.Back
    ) {
        return null;
    }

    const otherBox = new THREE.Box3().setFromObject(otherProduct);
    const selectedBox = new THREE.Box3().setFromObject(cabinetGroup);
    const selectedZToMin = selectedBox.min.z - cabinetGroup.position.z;
    const selectedZToMax = selectedBox.max.z - cabinetGroup.position.z;

    return currentSnappingDir === SnapDir.Front
        ? otherBox.max.z - selectedZToMin
        : otherBox.min.z - selectedZToMax;
};

/**
 * Distance across the floor, ignoring height. A product that sits on the
 * floor under a cabinet is a toe kick below it, so a 3D distance would put
 * every cabinet out of range at the default toe kick.
 * @param {THREE.Vector3} a One position.
 * @param {THREE.Vector3} b The other.
 * @return {number} The distance between them in plan.
 */
export const computePlanDistance = (a: THREE.Vector3, b: THREE.Vector3) =>
    Math.hypot(a.x - b.x, a.z - b.z);

// Kicker frames and kick-length panels resting under a cabinet are measured
// in getSnappingData with computePlanDistance instead, so the X-only rule
// below no longer applies to them.
export const computeSnapDistances = (input: ComputeSnapDistancesInput) => {
    const {
        proposedPos,
        distancePos,
        kickerFrontDistancePos,
        kickerFrontRightDistancePos,
        targetPos,
        isKickLength,
        selectedCabinetDirection,
        currentSnappingDir,
    } = input;

    const distance =
        isKickLength &&
        selectedCabinetDirection === 'right' &&
        (currentSnappingDir === SnapDir.Front ||
            currentSnappingDir === SnapDir.Back)
            ? Math.abs(targetPos.x - distancePos.x)
            : proposedPos.distanceTo(distancePos);

    const kickerDistance = proposedPos.distanceTo(kickerFrontDistancePos);
    const kickerRightDistance = proposedPos.distanceTo(
        kickerFrontRightDistancePos
    );

    const withinSnapDistance = [
        distance,
        kickerDistance,
        kickerRightDistance,
    ].some((d) => d <= DEFAULT_SNAPPING_DISTANCE);

    return {distance, withinSnapDistance};
};
